import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  applyToShift,
  confirmWorker,
  getMyApplicationForShift,
  listApplicants,
  listMyApplications,
  setApplicantInterest,
  withdrawApplication,
} from "@/features/applications/api/applicationsApi";
import type { Applicant, ShortlistStatus } from "@/features/applications/types";

export function useMyApplications() {
  return useQuery({ queryKey: ["applications", "mine"], queryFn: listMyApplications });
}

export function useMyApplicationForShift(shiftId: string | undefined) {
  return useQuery({
    queryKey: ["applications", "shift", shiftId],
    queryFn: () => getMyApplicationForShift(shiftId!),
    enabled: !!shiftId,
  });
}

export function useApplicants(shiftId: string | undefined) {
  return useQuery({
    queryKey: ["applicants", shiftId],
    queryFn: () => listApplicants(shiftId!),
    enabled: !!shiftId,
    // Worker may edit their profile / certs after applying. Backend serves
    // fresh data, but the venue's cache is unreachable from the worker's
    // session, so poll while the venue is on the page (paused in background
    // tabs). 20 s gives near-real-time feel without hammering the API.
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
}

export function useApply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shiftId: string) => applyToShift(shiftId),
    onSuccess: (_data, shiftId) => {
      // Worker-visible queries
      qc.invalidateQueries({ queryKey: ["applications"] });
      // Venue-visible queue gets a new applicant
      qc.invalidateQueries({ queryKey: ["applicants", shiftId] });
      // Shift detail may surface application_count
      qc.invalidateQueries({ queryKey: ["shifts", "detail", shiftId] });
    },
  });
}

export function useWithdraw() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (applicationId: string) => withdrawApplication(applicationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["applications"] });
      // We don't have shiftId here, so blanket-invalidate every applicant
      // queue. In practice the worker has none of those queries cached
      // (different role), so this is essentially a no-op for the worker but
      // safely refreshes any open venue tab that shares the cache.
      qc.invalidateQueries({ queryKey: ["applicants"] });
      qc.invalidateQueries({ queryKey: ["shifts"] });
    },
  });
}

export function useConfirmWorker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shiftId, applicationId }: { shiftId: string; applicationId: string }) =>
      confirmWorker(shiftId, applicationId),
    onMutate: async ({ shiftId, applicationId }) => {
      // Optimistic: instantly flip the chosen applicant to CONFIRMED and
      // move the rest to PASSED so the venue's queue updates without waiting
      // for the network roundtrip + refetch.
      const key = ["applicants", shiftId];
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Applicant[]>(key);
      if (previous) {
        qc.setQueryData<Applicant[]>(
          key,
          previous.map((a) =>
            a.application_id === applicationId
              ? { ...a, status: "CONFIRMED" }
              : a.status === "CONFIRMED" || a.status === "WITHDRAWN"
                ? a
                : { ...a, status: "PASSED" },
          ),
        );
      }
      return { previous, key };
    },
    onError: (_err, _vars, ctx) => {
      // Roll back the optimistic write on failure.
      if (ctx?.previous) qc.setQueryData(ctx.key, ctx.previous);
    },
    onSettled: (_data, _err, { shiftId }) => {
      // Server is the source of truth — refetch every surface that depends
      // on the application/shift state on either actor's side.
      qc.invalidateQueries({ queryKey: ["applicants", shiftId] });
      qc.invalidateQueries({ queryKey: ["applications"] });
      qc.invalidateQueries({ queryKey: ["shifts"] });
      qc.invalidateQueries({ queryKey: ["shift-events", shiftId] });
    },
  });
}

export function useSetApplicantInterest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      shiftId,
      applicationId,
      interest,
    }: {
      shiftId: string;
      applicationId: string;
      interest: ShortlistStatus;
    }) => setApplicantInterest(shiftId, applicationId, interest),
    onMutate: async ({ shiftId, applicationId, interest }) => {
      // Optimistic: flip just this row to the new shortlist status.
      const key = ["applicants", shiftId];
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Applicant[]>(key);
      if (previous) {
        qc.setQueryData<Applicant[]>(
          key,
          previous.map((a) =>
            a.application_id === applicationId ? { ...a, status: interest } : a,
          ),
        );
      }
      return { previous, key };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(ctx.key, ctx.previous);
    },
    onSettled: (_data, _err, { shiftId }) => {
      qc.invalidateQueries({ queryKey: ["applicants", shiftId] });
      // Worker's app list shows shortlist status — refresh so they see they
      // were marked Interested.
      qc.invalidateQueries({ queryKey: ["applications"] });
    },
  });
}
